const RGBAtoRGB = (fc, bc) => {
  // fc and bc are hex values, without the leading #
  const fa = {
    r: hexToDec(fc.substr(0, 2)),
    g: hexToDec(fc.substr(2, 2)),
    b: hexToDec(fc.substr(4, 2)),
    a: hexToDec(fc.substr(6, 2)) / 255,
  };
  const ba = {
    r: hexToDec(bc.substr(0, 2)),
    g: hexToDec(bc.substr(2, 2)),
    b: hexToDec(bc.substr(4, 2)),
  };
  var a = fa.a;
  if (isNaN(a)) a = 1;
  var modR = Math.round((1 - a) * ba.r + a * fa.r);
  var modG = Math.round((1 - a) * ba.g + a * fa.g);
  var modB = Math.round((1 - a) * ba.b + a * fa.b);
  var modColor =
    ('0' + modR.toString(16)).slice(-2) +
    ('0' + modG.toString(16)).slice(-2) +
    ('0' + modB.toString(16)).slice(-2);
  return modColor;
};

const hexToRgba = color => {
  if (color.startsWith('#')) {
    color = color.substr(1);
  }
   // If the color is 6 characters (no alpha), add a default alpha value of 1
  if (color.length === 6) {
    color += 'FF'; // Add full opacity (alpha = 1)
  }

  return {
    r: hexToDec(color.substr(0, 2)),
    g: hexToDec(color.substr(2, 2)),
    b: hexToDec(color.substr(4, 2)),
    a: hexToDec(color.substr(6, 2)) / 255,
  };
};

const hexToRgbaArray = color => {
  const rgb = hexToRgba(color);
  if (rgb.a) {
    return [rgb.r, rgb.g, rgb.b, rgb.a];
  }
  return [rgb.r, rgb.g, rgb.b];
};

const hexToDec = c => {
  if (RegExp(/^[a-f|0-9]+$/i).test(c)) {
    return parseInt(c, 16);
  }
};

const getsRGB = c => {
  c = hexToDec(c) / 255;
  c = c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  return c;
};

const getLuminance = c => {
  return (
    0.2126 * getsRGB(c.substr(0, 2)) +
    0.7152 * getsRGB(c.substr(2, 2)) +
    0.0722 * getsRGB(c.substr(4, 2))
  );
};

const getContrastRatio = (bc, fc) => {
  if (bc?.startsWith('#')) {
    bc = bc.substr(1);
  }
  if (fc?.startsWith('#')) {
    fc = fc.substr(1);
  }

  if (fc === "fff") {
    fc = "FFFFFF"
  } else if (fc === "000") {
    fc = "000000"
  }

  const modifiedFc = RGBAtoRGB(fc, bc); //modifiedFc is the color you get when you put foreground color on top of background color and take into account the alpha value of foreground color
  const L1 = getLuminance(modifiedFc);
  const L2 = getLuminance(bc);
  const ratio = (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05);

  return {
    AA: ratio > 4.5,
    AAA: ratio > 7,
    AALG: ratio > 3,
    AAALG: ratio > 4.5,
  };
};

const blendColors = (...rest) => {
  const args = Array.from(rest);
  let base = [0, 0, 0, 0];
  let mix;
  let added;
  while (added = args.shift()) {
    if (typeof added[3] === 'undefined') {
      added[3] = 1;
    }
    // check if both alpha channels exist.
    if (base[3] && added[3]) {
      mix = [0, 0, 0, 0];
      // alpha
      mix[3] = 1 - (1 - added[3]) * (1 - base[3]);
      // red
      mix[0] = Math.round(
        (added[0] * added[3]) / mix[3] + (base[0] * base[3] * (1 - added[3])) / mix[3],
      );
      // green
      mix[1] = Math.round(
        (added[1] * added[3]) / mix[3] + (base[1] * base[3] * (1 - added[3])) / mix[3],
      );
      // blue
      mix[2] = Math.round(
        (added[2] * added[3]) / mix[3] + (base[2] * base[3] * (1 - added[3])) / mix[3],
      );
    } else if (added) {
      mix = added;
    } else {
      mix = base;
    }
    base = mix;
  }


  return mix;
};

function normalizeColor(value) {
  if (!value || value === 'transparent') {
    return '#00000000';
  }
  return value;
}


export function extractColorInfo(data) {
  const results = [];
  let count = 0;
  if (!data || !Array.isArray(data.flows)) {
    return results;
  }

  const baseFontSize = data?.style?.fontSize ?? null;

  data.flows.forEach(flow => {
    const pageBg = normalizeColor(flow?.style?.backgroundColor);

        const flowFontSize = flow?.style?.fontSize ?? baseFontSize;

    if (!Array.isArray(flow.originalblocks)) return;

    flow.originalblocks.forEach(block => {
      const blockBg = normalizeColor(block?.style?.backgroundColor);
      const blockFontSize = block?.style?.fontSize ?? flowFontSize;

      if (!Array.isArray(block.contents)) return;

      block.contents.forEach(contentItem => {
        const innerContents = Array.isArray(contentItem?.contents)
          ? contentItem.contents
          : [contentItem];

        innerContents.forEach(inner => {
          const thirdLevelContents = Array.isArray(inner?.contents)
            ? inner.contents
            : [inner];

          thirdLevelContents.forEach(third => {
            const fourthLevelContents = Array.isArray(third?.contents)
              ? third.contents
              : [third];

            fourthLevelContents.forEach(deepItem => {
              const content = deepItem?.content;
              if (!content || content === '\n') return;
              count= content !== '\n'? ++count:count;
              const style = deepItem?.style || {};
              const textBg = normalizeColor(style.backgroundColor);
              const textColor = normalizeColor(style.color || '#000000');
              const fontSize = style.fontSize ?? blockFontSize;

              if (deepItem.contentType === 'PARAGRAPH') {
                results.push({
                  name: content,
                  pageBg,
                  blockBg,
                  textBg,
                  textColor,
                  pageNumber: flow?.orderNumber ?? null,
                  fontSize,
                  blockUUID: block?.uuid,
                });
              }
            });
          });
        });
      });
    });
  });
  const resultObj={
  totalCount: count,
  result:results
 }
  return resultObj;
}

const rgbaToHex = (rgba) => {
  const r = Math.round(rgba[0]);
  const g = Math.round(rgba[1]);
  const b = Math.round(rgba[2]);
  const a = rgba[3];

  // Convert the RGB values to a two-digit hex string
  const hex = `#${((1 << 24) + (r << 16) + (g << 8) + b)
    .toString(16)
    .slice(1)}`;

  if (a !== undefined && a !== 1) {
    // If alpha is not 1, convert alpha to a 2-digit hex string
    const alphaHex = Math.round(a * 255).toString(16).padStart(2, '0');
    return hex + alphaHex; // Add alpha to hex color string
  }

  return hex; // Return RGB hex color if alpha is 1 or undefined
};


// Run checks
export function calculateContrastRatio(colorsArr) {
  return colorsArr.map(sample => {
    const textBgAlpha = hexToRgba(sample.textBg);
    const blockBgAlpha = hexToRgba(sample.blockBg);
    const pageBgAlpha = hexToRgba(sample.pageBg);

    const pageBgRgbaArr = hexToRgbaArray(sample.pageBg);
    const blockBgRgbaArr = hexToRgbaArray(sample.blockBg);
    const textBgRgbaArr = hexToRgbaArray(sample.textBg);

    let contrastRatio = null;
    let bgColor = null;

   if (textBgAlpha.a === 1) {
      contrastRatio = getContrastRatio(sample.textBg, sample.textColor);
      bgColor = sample.textBg;
    } else if (blockBgAlpha.a === 1) {
      let mix;
      if (textBgAlpha.a === 0) {
        mix = blockBgRgbaArr;
      } else {
        mix = blendColors(blockBgRgbaArr, textBgRgbaArr);
      }
      contrastRatio = getContrastRatio(rgbaToHex(mix), sample.textColor);
      bgColor = rgbaToHex(mix);
    } else if (pageBgAlpha.a === 1) {
      let mix;
      if (blockBgAlpha.a === 0 && textBgAlpha.a === 0) {
        mix = pageBgRgbaArr;
      } else {
        mix = blendColors(pageBgRgbaArr, blockBgRgbaArr, textBgRgbaArr);
      }
      contrastRatio = getContrastRatio(rgbaToHex(mix), sample.textColor);
      bgColor = rgbaToHex(mix);
    } else {
      let mix;
      if (pageBgRgbaArr.a === 0 && blockBgAlpha.a === 0 && textBgAlpha.a === 0) {
        mix = '#ffffff';
      } else {
        mix = blendColors(hexToRgbaArray('#ffffff'), pageBgRgbaArr, blockBgRgbaArr, textBgRgbaArr);
      }
      contrastRatio = getContrastRatio(rgbaToHex(mix), sample.textColor);
      bgColor = rgbaToHex(mix);
    }

    contrastRatio.pageNumber = sample.pageNumber;
    contrastRatio.textColor = sample.textColor;
    contrastRatio.bgColor = bgColor;
    contrastRatio.content = sample.name;
    contrastRatio.fontSize = sample.fontSize;
      const content = sample.name?.trim();
      const words = content ? content.split(/\s+/).filter(Boolean) : [];
      contrastRatio.content = words.length > 0
          ? words.slice(0, 3).join(' ')
          : "ABC";
    contrastRatio.blockUUID = sample.blockUUID;
    return contrastRatio;
  });
}

const localMap = {
  // Local map of colors to their rgba and rgbaArray
  ['#000000']: {
    rgba: hexToRgba('#000000'),
    rgbaArr: hexToRgbaArray('#000000')
  },
  ['#ffffff']: {
    rgba: hexToRgba('#ffffff'),
    rgbaArr: hexToRgbaArray('#ffffff')
  }
}

export function checkContrastRatio(sample, fontSize) {

    let textBgAlpha, textBgRgbaArr;
    let blockBgAlpha, blockBgRgbaArr;
    let pageBgAlpha, pageBgRgbaArr;

    if (localMap[sample.textBg]) {
      textBgAlpha = localMap[sample.textBg].rgba;
      textBgRgbaArr = localMap[sample.textBg].rgbaArr;
    } else {
      textBgAlpha = hexToRgba(sample.textBg);
      textBgRgbaArr = hexToRgbaArray(sample.textBg);
      localMap[sample.textBg] = {
        rgba: textBgAlpha,
        rgbaArr: textBgRgbaArr
      }
    }

    if (localMap[sample.blockBg]) {
      blockBgAlpha = localMap[sample.blockBg].rgba;
      blockBgRgbaArr = localMap[sample.blockBg].rgbaArr;
    } else {
      blockBgAlpha = hexToRgba(sample.blockBg);
      blockBgRgbaArr = hexToRgbaArray(sample.blockBg);
      localMap[sample.blockBg] = {
        rgba: blockBgAlpha,
        rgbaArr: blockBgRgbaArr
      }
    }

    if (localMap[sample.pageBg]) {
      pageBgAlpha = localMap[sample.pageBg].rgba;
      pageBgRgbaArr = localMap[sample.pageBg].rgbaArr;
    } else {
      pageBgAlpha = hexToRgba(sample.pageBg);
      pageBgRgbaArr = hexToRgbaArray(sample.pageBg);
      localMap[sample.pageBg] = {
        rgba: pageBgAlpha,
        rgbaArr: pageBgRgbaArr
      }
    }

    let contrastRatio = null;
    let bgColor = null;

   if (textBgAlpha.a === 1) {
      contrastRatio = getContrastRatio(sample.textBg, sample.textColor);
      bgColor = sample.textBg;
    } else if (blockBgAlpha.a === 1) {
      let mix;
      if (textBgAlpha.a === 0) {
        mix = blockBgRgbaArr;
      } else {
        mix = blendColors(blockBgRgbaArr, textBgRgbaArr);
      }
      contrastRatio = getContrastRatio(rgbaToHex(mix), sample.textColor);
      bgColor = rgbaToHex(mix);
    } else if (pageBgAlpha.a === 1) {
      let mix;
      if (blockBgAlpha.a === 0 && textBgAlpha.a === 0) {
        mix = pageBgRgbaArr;
      } else {
        mix = blendColors(pageBgRgbaArr, blockBgRgbaArr, textBgRgbaArr);
      }
      contrastRatio = getContrastRatio(rgbaToHex(mix), sample.textColor);
      bgColor = rgbaToHex(mix);
    } else {
      let mix;
      if (pageBgAlpha.a === 0 && blockBgAlpha.a === 0 && textBgAlpha.a === 0) {
        mix = localMap['#ffffff'].rgbaArr;
      } else {
        mix = blendColors(localMap['#ffffff'].rgbaArr, pageBgRgbaArr, blockBgRgbaArr, textBgRgbaArr);
      }
      bgColor = rgbaToHex(mix);
      contrastRatio = getContrastRatio(bgColor, sample.textColor);
    }

    return { fail: fontSize > 14 ? contrastRatio.AAA === false : contrastRatio.AA === false, bgColor };
}

